import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../apiClient";
import { useAuth } from "../auth/AuthContext";
import { subscribeToNewBookEvents } from "../bookEventsStream";

const AUTO_HIDE_MS = 8000;

export default function NewBookNotificationHost() {
  const { token, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return undefined;
    }

    const controller = new AbortController();

    const run = () => {
      subscribeToNewBookEvents({
        url: `${API_BASE_URL}/books/events`,
        token,
        signal: controller.signal,
        onEvent: (payload) => {
          setNotice({ id: payload.id, title: payload.title });
        },
        onUnauthorized: () => {
          logout();
          navigate("/login", { replace: true });
        },
        onError: (err) => {
          if (!controller.signal.aborted) {
            console.warn("Book events stream error:", err);
          }
        },
      });
    };

    run();

    return () => {
      controller.abort();
    };
  }, [token, isAuthenticated, logout, navigate]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = setTimeout(() => {
      setNotice(null);
      hideTimerRef.current = null;
    }, AUTO_HIDE_MS);
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [notice]);

  const dismiss = () => {
    setNotice(null);
  };

  if (!notice) {
    return null;
  }

  return (
    <div className="new-book-toast" role="status" aria-live="polite">
      <div className="new-book-toast-inner">
        <span className="new-book-toast-label">New book added</span>
        <Link className="new-book-toast-link" to={`/books/${notice.id}`}>
          {notice.title}
        </Link>
        <button
          type="button"
          className="new-book-toast-dismiss"
          onClick={dismiss}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}
